import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, X, RefreshCw } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const EMPTY = {
  tier_key: '', name: '', tagline: '', description: '',
  price: 0, currency: 'usd', interval: 'year',
  discount_percent: 0, stay_credit_days: 0, highlight: false,
  discount_percent_meditations: 0, discount_percent_shop: 0, discount_percent_sessions: 0, stay_discount_percent: 0,
  is_active: true, sort_order: 0, benefits: [],
};

// Canonical tier keys the discount/benefit engine understands. Using a dropdown
// prevents typos (e.g. "Gold ", "diamond" vs "diamond1") that silently break member discounts.
const TIER_KEY_OPTIONS = [
  ['gold', 'gold — Gold / Sangha'],
  ['diamond1', 'diamond1 — Diamond'],
  ['lifetime', 'lifetime — Lifetime'],
  ['meditators', 'meditators — Meditators'],
];

const AdminMemberships = () => {
  const { toast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan object or 'new'
  const [form, setForm] = useState(EMPTY);
  const [benefitsText, setBenefitsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [basic, setBasic] = useState({ name: '', tagline: '', benefits: [] });
  const [basicBenefits, setBasicBenefits] = useState('');
  const [basicSaving, setBasicSaving] = useState(false);
  const [customTier, setCustomTier] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/memberships/plans');
      setPlans(res.data || []);
    } catch (e) { console.error('Failed to load memberships:', e); }
    finally { setLoading(false); }
  };

  const fetchBasic = async () => {
    try {
      const res = await api.get('/admin/memberships/basic');
      setBasic(res.data);
      setBasicBenefits((res.data.benefits || []).join('\n'));
    } catch (e) { console.error('Failed to load basic tier:', e); }
  };

  useEffect(() => { fetchPlans(); fetchBasic(); }, []);

  const saveBasic = async () => {
    setBasicSaving(true);
    try {
      await api.put('/admin/memberships/basic', {
        name: basic.name, tagline: basic.tagline,
        benefits: basicBenefits.split('\n').map((b) => b.trim()).filter(Boolean),
      });
      toast({ title: 'Free tier updated' });
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail }); }
    finally { setBasicSaving(false); }
  };

  const openNew = () => {
    setForm(EMPTY);
    setBenefitsText('');
    setCustomTier(false);
    setEditing('new');
  };

  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p });
    setBenefitsText((p.benefits || []).join('\n'));
    setCustomTier(false);
    setEditing(p);
  };

  const closeForm = () => { setEditing(null); setForm(EMPTY); setBenefitsText(''); };

  const save = async () => {
    if (!form.name || !form.tier_key) {
      return toast({ title: 'Missing fields', description: 'Name and tier key are required.' });
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      discount_percent: parseInt(form.discount_percent, 10) || 0,
      stay_credit_days: parseInt(form.stay_credit_days, 10) || 0,
      sort_order: parseInt(form.sort_order, 10) || 0,
      benefits: benefitsText.split('\n').map((b) => b.trim()).filter(Boolean),
    };
    try {
      if (editing === 'new') {
        await api.post('/admin/memberships/plans', payload);
        toast({ title: 'Plan created' });
      } else {
        await api.patch(`/admin/memberships/plans/${editing.id}`, payload);
        toast({ title: 'Plan updated' });
      }
      closeForm();
      fetchPlans();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this plan? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/memberships/plans/${id}`);
      toast({ title: 'Plan deleted' });
      fetchPlans();
    } catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail }); }
  };

  const reconcileTiers = async () => {
    setReconciling(true);
    try {
      const res = await api.post('/admin/memberships/reconcile-tiers');
      const { migrated_total = 0, orphans = [] } = res.data || {};
      toast({
        title: migrated_total > 0 ? `Migrated ${migrated_total} member(s)` : 'All members already up to date',
        description: orphans.length
          ? `${orphans.length} member(s) still on an unknown tier — review: ${orphans.slice(0, 3).map((o) => o.email).join(', ')}${orphans.length > 3 ? '…' : ''}`
          : 'Every member maps to a valid plan.',
        variant: orphans.length ? 'destructive' : undefined,
      });
    } catch (err) { toast({ title: 'Reconcile failed', description: err?.response?.data?.detail }); }
    finally { setReconciling(false); }
  };

  const field = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });
  const usedKeys = new Set(plans.map((p) => p.tier_key));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif-display text-4xl text-burgundy">Membership <span className="font-italic-display text-rose-italic">Plans</span></h1>
          <p className="mt-2 text-mahogany/80 max-w-2xl">Configure pricing, benefits, and tiers. Saving a plan automatically syncs its price with Stripe.</p>
        </div>
        <button onClick={openNew} className="pill-btn"><Plus className="w-4 h-4" /> New Plan</button>
      </div>

      <div className="flex items-center gap-3 flex-wrap p-4 rounded-2xl border border-rose/40 bg-white/60" data-testid="tier-reconcile-bar">
        <div className="text-sm text-mahogany/80">
          <span className="font-medium text-burgundy">Tier check:</span> fix members left on a renamed/legacy tier so their discounts apply.
        </div>
        <button onClick={reconcileTiers} disabled={reconciling} data-testid="reconcile-tiers-btn"
          className="pill-btn-outline text-sm inline-flex items-center gap-1.5 ml-auto disabled:opacity-60">
          {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reconcile tiers
        </button>
      </div>

      {/* Editable free tier */}
      <div className="p-5 rounded-2xl border border-rose/50 bg-white/70" data-testid="basic-tier-editor">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-mahogany/70 mb-3">Free tier (shown as the first card on the public membership page)</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Input label="Free tier name" value={basic.name} onChange={(e) => setBasic({ ...basic, name: e.target.value })} data-testid="basic-name-input" />
          <Input label="Tagline" value={basic.tagline} onChange={(e) => setBasic({ ...basic, tagline: e.target.value })} data-testid="basic-tagline-input" />
        </div>
        <label className="block mt-3">
          <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">Benefits (one per line)</span>
          <textarea value={basicBenefits} onChange={(e) => setBasicBenefits(e.target.value)} rows={3} data-testid="basic-benefits-input"
            className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" />
        </label>
        <button onClick={saveBasic} disabled={basicSaving} data-testid="basic-save" className="pill-btn mt-3">
          {basicSaving && <Loader2 className="w-4 h-4 animate-spin" />} Save free tier
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <ul className="grid md:grid-cols-2 gap-4">
          {plans.map((p) => (
            <li key={p.id} className={`p-5 rounded-2xl border ${p.is_active ? 'border-rose/50 bg-white/70' : 'border-mahogany/20 bg-mahogany/5 opacity-70'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-serif-display text-xl text-burgundy">{p.name}</div>
                  <div className="text-sm text-mahogany/70">{p.tier_key} · {p.price > 0 ? `$${p.price} ${p.interval}` : 'Free'}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(p)} className="w-9 h-9 rounded-full bg-rose-soft/60 flex items-center justify-center text-mahogany hover:bg-rose-soft"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.id)} className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center text-red-600 hover:bg-red-100"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                {p.discount_percent > 0 && <span className="px-2 py-1 rounded-full bg-rose-soft text-mahogany uppercase tracking-widest">{p.discount_percent}% off</span>}
                {p.highlight && <span className="px-2 py-1 rounded-full bg-mahogany text-cream uppercase tracking-widest">Featured</span>}
                {!p.is_active && <span className="px-2 py-1 rounded-full bg-mahogany/10 text-mahogany uppercase tracking-widest">Inactive</span>}
                {p.stripe_price_id && <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 uppercase tracking-widest">Stripe linked</span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeForm}>
          <div className="bg-cream rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif-display text-2xl text-burgundy">{editing === 'new' ? 'New Plan' : 'Edit Plan'}</h2>
              <button onClick={closeForm} className="text-mahogany/70 hover:text-mahogany"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input label="Name*" {...field('name')} />
                {editing === 'new' ? (
                  <label className="block">
                    <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">Tier Key*</span>
                    {!customTier ? (
                      <select
                        value={form.tier_key}
                        data-testid="tier-key-select"
                        onChange={(e) => {
                          if (e.target.value === '__custom__') { setCustomTier(true); setForm({ ...form, tier_key: '' }); }
                          else setForm({ ...form, tier_key: e.target.value });
                        }}
                        className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none">
                        <option value="" disabled>Select a tier…</option>
                        {TIER_KEY_OPTIONS.map(([v, l]) => {
                          const used = usedKeys.has(v);
                          return <option key={v} value={v} disabled={used}>{l}{used ? ' (in use)' : ''}</option>;
                        })}
                        <option value="__custom__">Custom — type a new key…</option>
                      </select>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          value={form.tier_key}
                          data-testid="tier-key-custom"
                          onChange={(e) => setForm({ ...form, tier_key: e.target.value.trim().toLowerCase() })}
                          placeholder="e.g. sangha"
                          className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" />
                        <button type="button" data-testid="tier-key-pick-list" onClick={() => { setCustomTier(false); setForm({ ...form, tier_key: '' }); }}
                          className="text-xs whitespace-nowrap px-3 rounded-xl bg-rose-soft/60 text-mahogany hover:bg-rose-soft">List</button>
                      </div>
                    )}
                  </label>
                ) : (
                  <label className="block">
                    <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">Tier Key</span>
                    <input value={form.tier_key} disabled data-testid="tier-key-locked"
                      className="w-full bg-mahogany/5 rounded-xl border border-rose/50 px-3 py-2 text-mahogany/70 cursor-not-allowed focus:outline-none" />
                    <span className="block text-[10px] text-mahogany/60 mt-1">Locked — changing this would disconnect existing members.</span>
                  </label>
                )}
              </div>
              <Input label="Tagline" {...field('tagline')} />
              <div className="grid grid-cols-3 gap-3">
                <Input label="Price" type="number" {...field('price')} />
                <Input label="Currency" {...field('currency')} />
                <Select label="Interval" {...field('interval')} options={[['year', 'Yearly'], ['month', 'Monthly'], ['one_time', 'One-time']]} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input label="Discount % — Retreats/Events" type="number" {...field('discount_percent')} />
                <Input label="Stay Credit Days" type="number" {...field('stay_credit_days')} />
                <Input label="Sort Order" type="number" {...field('sort_order')} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-mahogany/70 mb-1.5">Discounts applied at checkout (% off) — set per area</p>
                <div className="grid grid-cols-3 gap-3">
                  <Input label="Meditations %" type="number" {...field('discount_percent_meditations')} />
                  <Input label="Shop / Store %" type="number" {...field('discount_percent_shop')} />
                  <Input label="Private Sessions %" type="number" {...field('discount_percent_sessions')} />
                  <Input label="Stay / Accommodation %" type="number" {...field('stay_discount_percent')} />
                </div>
              </div>
              <BenefitsPreview form={form} />
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">Benefits (one per line)</span>
                <textarea value={benefitsText} onChange={(e) => setBenefitsText(e.target.value)} rows={4}
                  className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" />
              </label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-burgundy">
                  <input type="checkbox" checked={form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} /> Featured
                </label>
                <label className="flex items-center gap-2 text-sm text-burgundy">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
                </label>
              </div>
              <button onClick={save} disabled={saving} className="pill-btn justify-center w-full">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing === 'new' ? 'Create Plan' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">{label}</span>
    <input {...props} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none" />
  </label>
);

const Select = ({ label, options, ...props }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">{label}</span>
    <select {...props} className="w-full bg-white/70 rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

const n = (v) => Math.max(0, Math.min(100, parseInt(v, 10) || 0));
const saved = (pct, base) => (base * n(pct) / 100).toFixed(0);

// Live "what members save" summary so admins see the discounts stacking before saving.
const BenefitsPreview = ({ form }) => {
  const rows = [
    ['Retreats / Events', form.discount_percent],
    ['Meditations', form.discount_percent_meditations],
    ['Shop / Store', form.discount_percent_shop],
    ['Private Sessions', form.discount_percent_sessions],
    ['Stay / Accommodation', form.stay_discount_percent],
  ];
  const cur = (form.currency === 'inr' ? '₹' : '$');
  const stayDays = parseInt(form.stay_credit_days, 10) || 0;
  const anyDiscount = rows.some(([, p]) => n(p) > 0) || stayDays > 0;
  return (
    <div className="rounded-2xl border border-mahogany/20 bg-mahogany/5 p-4" data-testid="benefits-preview">
      <div className="text-xs uppercase tracking-[0.2em] text-mahogany/70 mb-3">What members save on this plan</div>
      {!anyDiscount ? (
        <p className="text-sm text-mahogany/60">No discounts set yet — add a % above to preview member savings.</p>
      ) : (
        <div className="space-y-1.5">
          {rows.map(([label, pct]) => (
            <div key={label} className="flex items-center justify-between text-sm" data-testid={`preview-row-${label}`}>
              <span className="text-mahogany/80">{label}</span>
              {n(pct) > 0 ? (
                <span className="text-burgundy font-medium">
                  {n(pct)}% off <span className="text-mahogany/60 font-normal">· save {cur}{saved(pct, 100)} on a {cur}100 order</span>
                </span>
              ) : <span className="text-mahogany/40">—</span>}
            </div>
          ))}
          {stayDays > 0 && (
            <div className="flex items-center justify-between text-sm pt-1.5 border-t border-mahogany/10" data-testid="preview-row-stay-credit">
              <span className="text-mahogany/80">Complimentary stay</span>
              <span className="text-burgundy font-medium">{stayDays} day{stayDays > 1 ? 's' : ''} free</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminMemberships;
